import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, UserPlus, Shield, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/Authcontext';
import { useTheme } from '../../contexts/Themecontext';
import { authAPI } from '../../services/api';
import logo from '../../assets/img/logo.png';

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Complete Signup
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(null);
  
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Step 1: Request Signup OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.requestSignupOTP({ email: formData.email });
      
      setStep(2);
      setOtpExpiry(response.otp_expiry_min || 10);
    } catch (err) {
      console.error('OTP request error:', err);
      setError(
        err.response?.data?.error || 
        'Failed to send OTP. Please try again.'
      );
      
      if (err.response?.status === 429) {
        const remaining = err.response?.data?.remaining;
        setError(`Too many attempts. Please try again in ${remaining} minutes.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Complete Signup with OTP
  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.signup({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        otp: otp,
      });

      const { user, access_token, refresh_token } = response;
      
      if (!user) {
        throw new Error("Signup successful but user data is missing");
      }

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Update auth context
      login(user, access_token);

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp('');
    setStep(1);
    setError('');
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-blue-500',
        button: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500',
        accent: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        card: 'bg-slate-800',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-cyan-500',
        button: 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500',
        accent: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      card: 'bg-slate-900/50 backdrop-blur-xl',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      accent: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`min-h-screen ${classes.bg} flex items-center justify-center px-4 py-12 pt-20 sm:px-6 lg:px-8`}>
      <div className="relative max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center">
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 mb-2">
            <span className={`text-3xl font-bold ${classes.accent}`}>GLY</span>
            <span className={`text-3xl font-bold ${theme === 'quantum' ? 'text-pink-400' : classes.accent}`}>QIX</span>
          </div>
          <h2 className={`text-3xl font-bold ${classes.text}`}>
            {step === 1 ? 'Create Account' : 'Verify Your Email'}
          </h2>
          <p className={`mt-2 ${classes.textMuted}`}>
            {step === 1 ? 'Join us today' : 'Enter the OTP sent to your email'}
          </p>
        </div>

        <div className={`${classes.card} border ${classes.border} rounded-2xl shadow-2xl p-8`}>
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Registration Form */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                    First Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className={`w-5 h-5 ${classes.textMuted}`} />
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                      placeholder="John"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className={`block w-full px-3 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className={`w-5 h-5 ${classes.textMuted}`} />
                    ) : (
                      <Eye className={`w-5 h-5 ${classes.textMuted}`} />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className={`w-5 h-5 ${classes.textMuted}`} />
                    ) : (
                      <Eye className={`w-5 h-5 ${classes.textMuted}`} />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <Shield size={20} />
                    Send Verification Code
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleCompleteSignup} className="space-y-6">
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${theme === 'quantum' ? 'bg-purple-500/20' : 'bg-blue-500/20'} mb-4`}>
                  <Shield size={32} className={classes.accent} />
                </div>
                <p className={`text-sm ${classes.textMuted}`}>
                  We've sent a 6-digit code to <strong>{formData.email}</strong>
                </p>
                {otpExpiry && (
                  <p className={`text-xs ${classes.textMuted} mt-2`}>
                    Code expires in {otpExpiry} minutes
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2 text-center`}>
                  Enter Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className={`block w-full px-3 py-4 border ${classes.input} rounded-lg focus:outline-none text-center tracking-widest text-2xl font-mono`}
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    Create Account
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className={`text-sm ${classes.accent} hover:opacity-80 font-medium transition-colors`}
                >
                  Didn't receive code? Try again
                </button>
              </div>
            </form>
          )}

          {/* Footer Links */}
          {step === 1 && (
            <div className="mt-6 text-center">
              <p className={`text-sm ${classes.textMuted}`}>
                Already have an account?{' '}
                <Link to="/login" className={`font-medium ${classes.accent} hover:opacity-80 transition-colors`}>
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>

        <div className="text-center">
          <p className={`text-xs ${classes.textMuted} flex items-center justify-center gap-2`}>
            <Shield size={14} />
            Protected by OTP email verification
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;